import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../../util_components/common_header';
import CustomModal from '../../../util_components/custom_modal';
import { image_error_replace } from '../../../../util_functions';
import { post_api as postApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import { RequestInfoIcon } from '../../../../utils/svg';
import * as actions from '../../../../redux/action_creators';
import { getTimezoneAbbr } from '../../../../constants';
import DateRangePicker from '../../../util_components/date_range_picker';
import ConfirmSessionDetails from './ConfirmSessionDetails';
import RequestSession from '../../students/request_session';
import './request_session_dashboard.css';

/**
 * Request Session Dashboard (care team) — Figma "Request Session Dashboard".
 *
 * Lists session requests created by the care team with their broadcast status, and lets care
 * resend / cancel a request and inspect which matched teachers have viewed/declined it.
 *
 * Wired to goserver (ops token):
 *   POST /v2/session_request/care/list      (A3) — rows
 *   POST /v2/session_request/care/activity  (A4) — teacher breakdown (info icon)
 *   POST /v2/session_request/care/resend    (A5) — re-broadcast
 *   POST /v2/session_request/care/cancel    (A6) — cancel
 */

const STATUS_STYLE = {
  pending: { bg: 'var(--badge-pending-bg)', color: 'var(--badge-pending-text)', label: 'Pending' },
  accepted: {
    bg: 'var(--badge-accepted-bg)',
    color: 'var(--badge-accepted-text)',
    label: 'Accepted'
  },
  cancelled: {
    bg: 'var(--badge-cancelled-bg)',
    color: 'var(--badge-cancelled-text)',
    label: 'Cancelled'
  },
  expired: {
    bg: 'var(--brand-primary-subtle-bg)',
    color: 'var(--brand-primary)',
    label: 'Expired'
  },
  failed: {
    bg: 'var(--badge-cancelled-bg)',
    color: 'var(--badge-cancelled-text)',
    label: 'Failed'
  }
};

const GENDER_LABEL = {
  MALE: 'Male',
  FEMALE: 'Female',
  NO_PREFERENCE: 'No Preference'
};

const COLUMNS = [
  'Requested By',
  'Request Date & Time',
  'Student Profile',
  'Class Type',
  'Student Date & Time',
  'Teacher Date & Time (IST)',
  'Accepted By',
  'Session status',
  'Request status',
  'Request Info',
  'Action'
];

const filter_select_style = {
  height: 38,
  padding: '0 34px 0 14px',
  borderRadius: 20,
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  color: 'var(--input-text)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  outline: 'none'
};

const th_style = {
  padding: '14px 10px',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textAlign: 'left',
  whiteSpace: 'nowrap'
};

const td_style = {
  padding: '14px 10px',
  fontSize: 12,
  color: 'var(--text-primary)',
  borderTop: '1px solid var(--border-default)',
  verticalAlign: 'middle',
  whiteSpace: 'pre-line'
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '7px 10px',
        borderRadius: 5,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '16px',
        background: s.bg,
        color: s.color
      }}
    >
      {s.label}
    </span>
  );
}

const format_request_time = (ms) => {
  if (!ms) return '-';
  const m = moment(ms).tz('Asia/Kolkata');
  if (!m.isValid()) return '-';
  return `${m.format('ddd, MMM D, YY')}\n${m.format('h:mm A')} IST`;
};

// Format the scheduled session time client-side in the given zone, with a friendly abbreviation
// (e.g. "SGT" not "+08"). Keeps timezone-abbr logic in one place (the frontend helper) instead of
// duplicating a map in goserver.
const format_session_time = (ms, zone) => {
  if (!ms) return '-';
  const tz = zone || 'Asia/Kolkata';
  const m = moment(ms).tz(tz);
  if (!m.isValid()) return '-';
  const abbr = getTimezoneAbbr(tz) || m.zoneAbbr();
  return `${m.format('ddd, MMM D, YYYY · h:mm A')} ${abbr}`;
};

// Default filter window — last 30 days of request (created) dates, matching the Figma pill.
const default_date_range = () => ({
  startDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD')
});

function RequestSessionDashboard() {
  const dispatch = useDispatch();
  const history = useHistory();
  // Goal list for the Edit form (create a modified request based on this one).
  const goalOptions = useSelector((s) => s.home.config_values?.goals_for_1on1_fitness);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(default_date_range);
  const [studentTypeFilter, setStudentTypeFilter] = useState('all'); // all | Trial | Member
  const [requestStateFilter, setRequestStateFilter] = useState('all'); // all | pending | accepted | completed | cancelled | expired

  const [infoRow, setInfoRow] = useState(null);
  const [activity, setActivity] = useState(null); // { not_viewed, viewed, declined }
  const [activityLoading, setActivityLoading] = useState(false);

  const [resendRow, setResendRow] = useState(null);
  const [editRow, setEditRow] = useState(null); // row being edited (Edit → prefilled create form)
  const [actionUuid, setActionUuid] = useState(null); // uuid whose resend/cancel is in flight

  const notify = useCallback(
    (type, message) => dispatch(actions.set_notification_variable(true, type, message)),
    [dispatch]
  );

  const err_message = (e, fallback) => {
    try {
      return e.response.data.message || e.response.data.reason;
    } catch (err) {
      return fallback;
    }
  };

  // silent = background poll: no loading flash, no error toast (transient failures shouldn't spam).
  const load_requests = useCallback(
    (silent = false) => {
      if (!silent) setLoading(true);
      // from_date/to_date are epoch_ms filtering on request created_date (0 => unbounded).
      const from_date = dateRange.startDate
        ? moment(dateRange.startDate).startOf('day').valueOf()
        : 0;
      const to_date = dateRange.endDate ? moment(dateRange.endDate).endOf('day').valueOf() : 0;
      const url = getApiUrl('/v2/session_request/care/list', false, true);
      postApi(url, { status: 'all', from_date, to_date, page: 0, item_limit: 200 }, true)
        .then((resp) => {
          setRows(resp.data?.data?.session_requests || []);
          if (!silent) setLoading(false);
        })
        .catch((e) => {
          if (!silent) {
            setLoading(false);
            notify('error', err_message(e, 'Could not load session requests.'));
          }
        });
    },
    [notify, dateRange]
  );

  useEffect(() => {
    load_requests();
  }, [load_requests]);

  // Auto-refresh so a teacher's acceptance flips the row to "Accepted" without a manual page
  // refresh (PROD-8621). Silent poll every 15s while the dashboard is open.
  useEffect(() => {
    const id = setInterval(() => load_requests(true), 15000);
    return () => clearInterval(id);
  }, [load_requests]);

  const open_teacher_profile = (uuid) =>
    history.push(`/teacherprofile?id=${encodeURIComponent(uuid)}`);

  const open_info = (row) => {
    setInfoRow(row);
    setActivity(null);
    setActivityLoading(true);
    const url = getApiUrl('/v2/session_request/care/activity', false, true);
    postApi(url, { session_request_uuid: row.uuid }, true)
      .then((resp) => {
        const data = resp.data?.data || {};
        const viewed_or_declined = data.viewed_or_declined || [];
        setActivity({
          not_viewed: data.not_viewed || [],
          viewed: viewed_or_declined.filter((t) => t.status === 'viewed'),
          declined: viewed_or_declined.filter((t) => t.status === 'declined')
        });
        setActivityLoading(false);
      })
      .catch((e) => {
        setActivityLoading(false);
        notify('error', err_message(e, 'Could not load request activity.'));
      });
  };

  const on_cancel = (row) => {
    setActionUuid(row.uuid);
    const url = getApiUrl('/v2/session_request/care/cancel', false, true);
    postApi(url, { session_request_uuid: row.uuid }, true)
      .then(() => {
        setActionUuid(null);
        notify('success', 'Session request cancelled.');
        load_requests();
      })
      .catch((e) => {
        setActionUuid(null);
        notify('error', err_message(e, 'Could not cancel the request.'));
      });
  };

  // Resend re-broadcasts the EXISTING request (care/resend) — it does NOT create a new one, so
  // there's no duplicate (PROD-8616).
  const on_confirm_resend = () => {
    if (!resendRow) return;
    const { uuid } = resendRow;
    setActionUuid(uuid);
    const url = getApiUrl('/v2/session_request/care/resend', false, true);
    postApi(url, { session_request_uuid: uuid }, true)
      .then((resp) => {
        const count = resp.data?.data?.teachers_count || 0;
        setActionUuid(null);
        setResendRow(null);
        notify('success', `Request re-sent to ${count} matched teacher${count === 1 ? '' : 's'}.`);
        load_requests();
      })
      .catch((e) => {
        setActionUuid(null);
        notify('error', err_message(e, 'Could not resend the request.'));
      });
  };

  // Prefill values for the Edit form (RequestSession) — create a modified request based on this
  // one. sessionDate is the student's wall-clock time rebuilt as a local Date so the picker
  // re-interprets it in the student's zone.
  const edit_initial = (row) => {
    const zone = row.student_timezone || moment.tz.guess();
    const wall = row.start_time ? moment.tz(row.start_time, zone).format('YYYY-MM-DDTHH:mm') : null;
    return {
      sessionDate: wall ? moment(wall).toDate() : null,
      duration: row.duration_minutes || undefined,
      coachGender: row.coach_gender || 'NO_PREFERENCE',
      languages: row.language
        ? row.language
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      goals: row.goals || [],
      specificNeeds: row.kind_of_session || ''
    };
  };

  // Map a list row into the shape the read-only resend-confirmation popup renders. Times are
  // formatted client-side (friendly tz abbr) like the dashboard columns.
  const to_confirm_row = (row) => ({
    student_zone_abbr:
      getTimezoneAbbr(row.student_timezone) ||
      (row.student_timezone ? moment.tz(row.student_timezone).zoneAbbr() : ''),
    student_time: format_session_time(row.start_time, row.student_timezone),
    teacher_time: format_session_time(row.start_time, 'Asia/Kolkata'),
    duration: row.duration_minutes ? `${row.duration_minutes} Mins` : '-',
    language: row.language || '-',
    gender: GENDER_LABEL[row.coach_gender] || row.coach_gender || '-',
    goals: row.goals || [],
    comment: row.kind_of_session || '-'
  });

  const render_status_row = (message, muted) => (
    <tr>
      <td
        style={{ ...td_style, textAlign: 'center', color: muted ? 'var(--text-muted)' : undefined }}
        colSpan={COLUMNS.length}
      >
        {message}
      </td>
    </tr>
  );

  const render_action = (row) => {
    if (row.status === 'accepted' || row.status === 'expired' || row.status === 'failed') {
      return <span style={{ color: 'var(--text-muted)' }}>-</span>;
    }
    const busy = actionUuid === row.uuid;
    const cancelDisabled = row.status !== 'pending' || busy;
    return (
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          type="button"
          style={{ ...resendBtn, opacity: busy ? 0.5 : 1, cursor: busy ? 'wait' : 'pointer' }}
          disabled={busy}
          onClick={() => setResendRow(row)}
        >
          Resend
        </button>
        <button
          type="button"
          disabled={cancelDisabled}
          style={{
            ...cancelBtn,
            opacity: cancelDisabled ? 0.4 : 1,
            cursor: cancelDisabled ? 'not-allowed' : 'pointer'
          }}
          onClick={() => !cancelDisabled && on_cancel(row)}
        >
          Cancel
        </button>
      </div>
    );
  };

  // Top-filter matching (client-side over the fetched window). Request state combines request status
  // (pending/accepted/cancelled/expired) with the derived session outcome (completed = session
  // Finished; cancelled also covers a teacher/student session cancellation).
  const matches_request_state = (row) => {
    switch (requestStateFilter) {
      case 'pending':
        return row.status === 'pending';
      case 'accepted':
        return row.status === 'accepted' && row.session_status !== 'Finished';
      case 'completed':
        return row.session_status === 'Finished';
      case 'cancelled':
        // Match the Request-status badge: only care-cancelled requests. A teacher/student session
        // cancellation flips the request back to Pending (shown under Pending, with the detail in
        // the Session-status column), so it isn't a "Cancelled request".
        return row.status === 'cancelled';
      case 'expired':
        return row.status === 'expired';
      default:
        return true;
    }
  };

  const filtered_rows = rows.filter(
    (row) =>
      (studentTypeFilter === 'all' || row.student_type === studentTypeFilter) &&
      matches_request_state(row)
  );

  return (
    <CommonHeader
      show_back
      title="Request Session Dashboard"
      class_name="request-session-dashboard-page"
    >
      <Helmet>
        <title>Request Session Dashboard</title>
      </Helmet>

      <div style={{ padding: '0 8px 40px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            margin: '4px 0 16px'
          }}
        >
          <select
            value={studentTypeFilter}
            onChange={(e) => setStudentTypeFilter(e.target.value)}
            style={filter_select_style}
            aria-label="Filter by student type"
          >
            <option value="all">All students</option>
            <option value="Trial">Trial</option>
            <option value="Member">Member</option>
          </select>
          <select
            value={requestStateFilter}
            onChange={(e) => setRequestStateFilter(e.target.value)}
            style={filter_select_style}
            aria-label="Filter by request state"
          >
            <option value="all">All states</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onApply={({ startDate, endDate }) => setDateRange({ startDate, endDate })}
            align="right"
            compact
          />
        </div>

        <div
          style={{
            overflowX: 'auto',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderRadius: 12
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000, margin: 0 }}>
            <thead>
              <tr style={{ background: 'var(--surface-sunken)' }}>
                {COLUMNS.map((c) => (
                  <th key={c} style={th_style}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && render_status_row('Loading…', false)}
              {!loading &&
                filtered_rows.length === 0 &&
                render_status_row(
                  rows.length === 0
                    ? 'No session requests yet.'
                    : 'No requests match the selected filters.',
                  true
                )}
              {!loading &&
                filtered_rows.map((row) => (
                  <tr key={row.uuid}>
                    <td style={td_style}>{row.requested_by_name || '-'}</td>
                    <td style={{ ...td_style, color: 'var(--text-muted)' }}>
                      {format_request_time(row.created_at)}
                    </td>
                    <td style={td_style}>
                      {row.student_uuid ? (
                        <a
                          href={`/studentprofile?id=${encodeURIComponent(row.student_uuid)}`}
                          onClick={(e) => {
                            e.preventDefault();
                            history.push(
                              `/studentprofile?id=${encodeURIComponent(row.student_uuid)}`
                            );
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: 'var(--brand-primary)',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <img
                            src={
                              row.student_profile_photo ||
                              'https://images.myyogateacher.com/user_profile/default-user.png'
                            }
                            onError={image_error_replace}
                            alt={row.student_name}
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                          {row.student_name || '-'}
                        </a>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <img
                            src={
                              row.student_profile_photo ||
                              'https://images.myyogateacher.com/user_profile/default-user.png'
                            }
                            onError={image_error_replace}
                            alt={row.student_name}
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                          {row.student_name || '-'}
                        </div>
                      )}
                    </td>
                    <td style={td_style}>{row.class_type || '1-on-1'}</td>
                    <td style={td_style}>
                      {format_session_time(row.start_time, row.student_timezone)}
                    </td>
                    <td style={td_style}>{format_session_time(row.start_time, 'Asia/Kolkata')}</td>
                    <td style={{ ...td_style, color: 'var(--text-muted)' }}>
                      {row.accepted_by_teacher_uuid && row.accepted_by_name ? (
                        <a
                          href={`/teacherprofile?id=${encodeURIComponent(
                            row.accepted_by_teacher_uuid
                          )}`}
                          onClick={(e) => {
                            e.preventDefault();
                            history.push(
                              `/teacherprofile?id=${encodeURIComponent(row.accepted_by_teacher_uuid)}`
                            );
                          }}
                          style={{
                            color: 'var(--brand-primary)',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          {row.accepted_by_name}
                        </a>
                      ) : (
                        row.accepted_by_name || '-'
                      )}
                    </td>
                    <td style={{ ...td_style, color: 'var(--text-muted)' }}>
                      {row.session_status || '-'}
                    </td>
                    <td style={td_style}>
                      <StatusBadge status={row.status} />
                    </td>
                    <td style={{ ...td_style, textAlign: 'center' }}>
                      <button
                        type="button"
                        aria-label="Request info"
                        onClick={() => open_info(row)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          padding: 0,
                          lineHeight: 0
                        }}
                      >
                        <RequestInfoIcon />
                      </button>
                    </td>
                    <td style={td_style}>{render_action(row)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <CustomModal
        show={!!resendRow}
        close={() => setResendRow(null)}
        inner_style={{ width: 688, maxWidth: '95vw', padding: '20px 40px 32px', borderRadius: 10 }}
      >
        {resendRow ? (
          <ConfirmSessionDetails
            row={to_confirm_row(resendRow)}
            confirming={actionUuid === resendRow.uuid}
            onEdit={() => {
              // Edit → open the prefilled Request-a-Session form (creates a modified request).
              setEditRow(resendRow);
              setResendRow(null);
            }}
            onConfirm={on_confirm_resend}
          />
        ) : null}
      </CustomModal>

      <CustomModal
        show={!!editRow}
        close={() => setEditRow(null)}
        inner_style={{ width: 688, maxWidth: '95vw', padding: '20px 40px 32px', borderRadius: 10 }}
      >
        {editRow ? (
          <RequestSession
            details={{ iana_timezone: editRow.student_timezone }}
            student_uuid={editRow.student_uuid}
            goal_options={goalOptions}
            initialValues={edit_initial(editRow)}
            initialStep="form"
            close={() => {
              setEditRow(null);
              load_requests();
            }}
            set_notification_variable={(show, type, message) =>
              dispatch(actions.set_notification_variable(show, type, message))
            }
          />
        ) : null}
      </CustomModal>

      <CustomModal
        show={!!infoRow}
        close={() => setInfoRow(null)}
        inner_style={{ width: 760, maxWidth: '94vw', padding: '20px 28px 28px', borderRadius: 14 }}
      >
        {infoRow ? (
          <div>
            <p
              style={{
                margin: '0 0 22px',
                fontSize: 22,
                fontWeight: 600,
                color: 'var(--text-primary)'
              }}
            >
              Session request activity
            </p>
            {activityLoading || !activity ? (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>Loading…</p>
            ) : (
              // Two columns side by side so care sees responses (Viewed/Declined) next to the
              // not-viewed list without scrolling past a long single column. Declined before viewed
              // within its group; each column scrolls independently when long.
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <TeacherGroup
                  title="Not viewed request"
                  teachers={activity.not_viewed}
                  onOpenTeacher={open_teacher_profile}
                />
                <TeacherGroup
                  title="Viewed / Declined request"
                  teachers={[...activity.declined, ...activity.viewed]}
                  showStatus
                  onOpenTeacher={open_teacher_profile}
                />
              </div>
            )}
          </div>
        ) : null}
      </CustomModal>
    </CommonHeader>
  );
}

const DEFAULT_AVATAR = 'https://images.myyogateacher.com/user_profile/default-user.png';

function TeacherGroup({ title, teachers, showStatus = false, onOpenTeacher }) {
  return (
    <div style={{ flex: 1, minWidth: 0, marginBottom: 22 }}>
      <p
        style={{
          margin: '0 0 10px',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)'
        }}
      >
        {title}
      </p>
      <div
        style={{
          background: 'var(--surface-sunken)',
          borderRadius: 12,
          padding: teachers.length ? '6px 16px' : '16px',
          maxHeight: 340,
          overflowY: 'auto'
        }}
      >
        {teachers.length ? (
          teachers.map((t) => (
            <div
              key={t.teacher_uuid}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0' }}
            >
              {/* Rendered as a background-image span (not <img>) to dodge the global
                `.custom-modal-inner-container img` !important override that would force width:auto. */}
              <span
                aria-label={t.name}
                style={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: 'var(--surface-sunken)',
                  backgroundImage: `url(${t.photo || DEFAULT_AVATAR})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              {t.teacher_uuid && onOpenTeacher ? (
                <a
                  href={`/teacherprofile?id=${encodeURIComponent(t.teacher_uuid)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenTeacher(t.teacher_uuid);
                  }}
                  className="rsd-teacher-link"
                  style={{ fontSize: 15 }}
                >
                  {t.name}
                </a>
              ) : (
                <span style={{ fontSize: 15, color: 'var(--text-primary)' }}>{t.name}</span>
              )}
              {showStatus && t.status ? (
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 12,
                    fontWeight: 600,
                    color: t.status === 'declined' ? 'var(--state-error)' : 'var(--text-muted)'
                  }}
                >
                  {t.status === 'declined' ? 'Declined' : 'Viewed'}
                </span>
              ) : null}
            </div>
          ))
        ) : (
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>—</span>
        )}
      </div>
    </div>
  );
}

const resendBtn = {
  padding: '10px 15px',
  borderRadius: 14,
  border: 'none',
  background: 'var(--action-dark-bg)',
  color: '#ffffff',
  fontSize: 14.5,
  fontWeight: 550,
  cursor: 'pointer',
  boxShadow: '0px 2px 4px 0px rgba(79, 42, 118, 0.2)'
};

const cancelBtn = {
  padding: '10px 15px',
  borderRadius: 14,
  border: '1px solid var(--border-default)',
  background: 'var(--surface-card)',
  color: 'var(--input-text)',
  fontSize: 14.5,
  fontWeight: 550,
  boxShadow: '0px 2px 4px 0px rgba(188, 188, 188, 0.2)'
};

export default RequestSessionDashboard;
